import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Gift, Cake, Clock, QrCode, Copy, Download } from "lucide-react";

export default function ProCrmSettings() {
  const [s, setS] = useState(null);
  const [link, setLink] = useState({ url: "", business_name: "" });
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/pro/crm/settings`).then((r) => setS(r.data)).catch(() => {});
    api.get(`/pro/crm/signup-link`).then((r) => setLink(r.data)).catch(() => {});
    api.get(`/pro/crm/qr.png`, { responseType: "blob" }).then((r) => setQr(URL.createObjectURL(r.data))).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try { await api.post(`/pro/crm/settings`, { welcome: s.welcome, birthday: s.birthday, winback: s.winback }); toast.success("Réglages enregistrés"); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const copyLink = async () => { try { await navigator.clipboard.writeText(link.url); toast.success("Lien copié"); } catch { toast.error("Copie impossible, copiez le lien manuellement."); } };

  if (!s) return <div className="max-w-3xl mx-auto text-slate-500">Chargement…</div>;
  const upd = (k, patch) => setS((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="crm-settings-page">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Collecte & automatisations</h1>
        <p className="text-sm text-slate-600 mt-1">Faites grossir votre liste et fidélisez sans effort.</p>
      </header>

      {/* QR / lien d'inscription */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-qr-block">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><QrCode size={18} className="text-[#E05638]" /> Lien « Rejoignez ma liste VIP »</div>
        <p className="text-sm text-slate-600 mt-1">Imprimez ce QR (vitrine, facture, devis, carte de visite, véhicule) ou partagez le lien. Vos clients s'inscrivent seuls.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-4 items-start">
          {qr && <img src={qr} alt="QR inscription" className="w-40 h-40 border border-slate-200 rounded-xl" data-testid="crm-qr-img" />}
          <div className="flex-1 w-full">
            <Label>Votre lien public</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={link.url} data-testid="crm-signup-url" />
              <Button variant="outline" size="icon" onClick={copyLink} data-testid="crm-copy-link"><Copy size={15} /></Button>
            </div>
            {qr && <a href={qr} download="qr-liste-vip.png" className="mt-2 inline-flex items-center gap-1 text-sm text-[#E05638] font-semibold"><Download size={14} /> Télécharger le QR</a>}
          </div>
        </div>
      </div>

      {/* Bienvenue */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-welcome-block">
        <div className="flex items-center justify-between">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Gift size={18} className="text-[#E05638]" /> Offre de bienvenue</div>
          <Switch checked={!!s.welcome?.enabled} onCheckedChange={(v) => upd("welcome", { enabled: v })} data-testid="crm-welcome-switch" />
        </div>
        <p className="text-sm text-slate-600 mt-1">Envoyée automatiquement après confirmation de l'inscription.</p>
        <Input className="mt-2" value={s.welcome?.offer || ""} onChange={(e) => upd("welcome", { offer: e.target.value })} placeholder="-10% sur votre prochaine visite" data-testid="crm-welcome-offer" />
      </div>

      {/* Anniversaire */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-birthday-block">
        <div className="flex items-center justify-between">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Cake size={18} className="text-pink-500" /> Message d'anniversaire</div>
          <Switch checked={!!s.birthday?.enabled} onCheckedChange={(v) => upd("birthday", { enabled: v })} data-testid="crm-birthday-switch" />
        </div>
        <p className="text-sm text-slate-600 mt-1">Envoyé le jour J aux contacts dont vous connaissez la date de naissance.</p>
        <Input className="mt-2" value={s.birthday?.offer || ""} onChange={(e) => upd("birthday", { offer: e.target.value })} placeholder="Un cadeau pour votre anniversaire 🎂" data-testid="crm-birthday-offer" />
      </div>

      {/* Reconquête */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="crm-winback-block">
        <div className="flex items-center justify-between">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2"><Clock size={18} className="text-amber-600" /> Reconquête des inactifs</div>
          <Switch checked={!!s.winback?.enabled} onCheckedChange={(v) => upd("winback", { enabled: v })} data-testid="crm-winback-switch" />
        </div>
        <p className="text-sm text-slate-600 mt-1">Relance automatique des clients sans activité depuis un certain temps.</p>
        <div className="mt-2 grid sm:grid-cols-3 gap-3">
          <div><Label>Après (jours)</Label><Input type="number" min={15} value={s.winback?.days || 60} onChange={(e) => upd("winback", { days: Number(e.target.value) })} data-testid="crm-winback-days" /></div>
          <div className="sm:col-span-2"><Label>Offre</Label><Input value={s.winback?.offer || ""} onChange={(e) => upd("winback", { offer: e.target.value })} data-testid="crm-winback-offer" /></div>
        </div>
      </div>

      <Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="crm-settings-save">Enregistrer</Button>
    </div>
  );
}
